import {useEffect} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {Trans, useTranslation} from "react-i18next"

import {SettlementOffer} from "./SettlementOffer"

import {TermsOfDiscountOffer} from "@indebted/components/TermsOfDiscountOffer"
import {useAnalytics} from "@indebted/analytics"

interface SettlementPlanOptionLinkProps {
	RequiresAffordabilityAssessment: boolean
	DiscountPercentage: string
	OutstandingBalanceWithDiscount: number
	DiscountValue: number
	StartDate: string
	EndDate: string
}

function SettlementPlanOptionLink({
	RequiresAffordabilityAssessment,
	DiscountPercentage,
	Currency,
	OutstandingBalanceWithDiscount,
	DiscountValue,
	StartDate,
	EndDate,
}: SettlementPlanOptionLinkProps & {Currency: string}) {
	const {secureCode} = useParams()
	const {track} = useAnalytics()
	const navigate = useNavigate()
	const {t, i18n} = useTranslation()

	useEffect(() => {
		track("PaymentPlanSettlementOfferViewed", {
			SecureCode: secureCode,
			Discount: DiscountPercentage,
		})
	}, [secureCode, track, DiscountPercentage])

	const TermsOfOffer = (
		<>
			<Trans
				i18nKey="customer_get.outstanding_balance.settlementplan.start_and_finish_dates"
				values={{
					StartDate: new Date(StartDate).toLocaleDateString(i18n.language, {
						year: "numeric",
						month: "long",
						day: "numeric",
					}),
					EndDate: new Date(EndDate).toLocaleDateString(i18n.language, {
						year: "numeric",
						month: "long",
						day: "numeric",
					}),
				}}
				components={{bold: <strong />}}
			/>{" "}
			<TermsOfDiscountOffer
				linkLabel={t(
					"customer_get.in_payment_plan.payment_plan_details.discount_message.terms_and_conditions_label",
				)}
			/>
		</>
	)

	return (
		<SettlementOffer
			HeaderDescription={t("customer_get.outstanding_balance.settlementplan.settle_in_payment_plan_today")}
			HeaderDiscount={t("customer_get.outstanding_balance.settlementplan.discount_off", {
				Discount: DiscountPercentage,
			})}
			BalanceToPay={t("customer_get.outstanding_balance.settlementplan.balance_to_pay")}
			OutstandingBalanceWithDiscountValue={t("customer_get.outstanding_balance.outstanding_balance_value", {
				OutstandingBalanceValue: OutstandingBalanceWithDiscount / 100,
				formatParams: {OutstandingBalanceValue: {currency: Currency}},
			})}
			SaveDiscountValue={t("customer_get.outstanding_balance.settlementplan.save_discount_value", {
				DiscountValue: DiscountValue / 100,
				formatParams: {DiscountValue: {currency: Currency}},
			})}
			ButtonLabel={t("customer_get.outstanding_balance.settlementplan.pay_with_discount", {
				Discount: DiscountPercentage,
			})}
			Footnote={t("customer_get.outstanding_balance.settlementplan.footnote")}
			ShowFootnote={true}
			onClick={() => {
				track("PPButtonClicked", {SecureCode: secureCode})
				if (RequiresAffordabilityAssessment) {
					navigate(`/${secureCode}/affordability-assessment/payment-plan/initiate`)
				} else {
					navigate(`/${secureCode}/payment-plan`)
				}
			}}
			TermsOfDiscountOffer={TermsOfOffer}
		/>
	)
}

export {SettlementPlanOptionLink}
